import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import ContactlessIcon from '@mui/icons-material/Contactless';
import QrScannerDialog from './QrScannerDialog';
import NfcScanDialog from './NfcScanDialog';
import { isNfcSupported } from './nfcLogin';

interface ReLoginDialogProps {
    login: (username: string, password: string) => Promise<string | null>;
    loginWithToken: (token: string) => Promise<string | null>;
    /**
     * i18n id explaining why the session ended, when the server rejected the
     * ACCOUNT (no roles, disabled) rather than the session merely expiring.
     * Signing back in as the same user won't help until an admin fixes it.
     */
    reasonId?: string;
}

export default function ReLoginDialog({
    login,
    loginWithToken,
    reasonId
}: ReLoginDialogProps) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);
    const [nfcOpen, setNfcOpen] = useState(false);
    const nfcSupported = isNfcSupported();

    async function handleSubmit(event: React.FormEvent) {
        event.preventDefault();
        setError(null);
        setLoading(true);

        const loginError = await login(username, password);
        if (loginError) {
            setError(loginError);
            setLoading(false);
        }
        // On success, sessionExpired becomes false and dialog unmounts
    }

    /** Log in with a token off a QR code or an NFC card — same credential. */
    async function redeemToken(token: string) {
        setScanOpen(false);
        setNfcOpen(false);
        setError(null);
        setLoading(true);
        const loginError = await loginWithToken(token);
        if (loginError) {
            setError(loginError);
            setLoading(false);
        }
    }

    return (
        <Dialog open>
            <DialogTitle>
                <FormattedMessage
                    id={
                        reasonId
                            ? 'auth.relogin.blockedTitle'
                            : 'auth.relogin.title'
                    }
                />
            </DialogTitle>
            {/* The one dialog with no buttons under it: the login form ends
                the box, so the divider the theme puts at the bottom of every
                DialogContent would be a hairline on the paper's edge. */}
            <DialogContent sx={{ borderBottom: 'none' }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        mb: reasonId ? 1 : 2
                    }}
                >
                    <FormattedMessage id={reasonId ?? 'auth.relogin.body'} />
                </Typography>
                {reasonId && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mb: 2
                        }}
                    >
                        <FormattedMessage id="auth.relogin.blockedHint" />
                    </Typography>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            label={<FormattedMessage id="auth.username" />}
                            value={username}
                            onChange={(event) =>
                                setUsername(event.target.value)
                            }
                            required
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label={<FormattedMessage id="auth.password" />}
                            type="password"
                            value={password}
                            onChange={(event) =>
                                setPassword(event.target.value)
                            }
                            required
                            fullWidth
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading || !username || !password}
                        >
                            {loading ? (
                                <FormattedMessage id="auth.login.loading" />
                            ) : (
                                <FormattedMessage id="auth.relogin.submit" />
                            )}
                        </Button>
                    </Stack>
                </form>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                    <Button
                        variant="outlined"
                        fullWidth
                        startIcon={<QrCodeScannerIcon />}
                        onClick={() => setScanOpen(true)}
                        disabled={loading}
                    >
                        <FormattedMessage id="auth.qr.scanButton" />
                    </Button>
                    {nfcSupported && (
                        <Button
                            variant="outlined"
                            fullWidth
                            startIcon={<ContactlessIcon />}
                            onClick={() => setNfcOpen(true)}
                            disabled={loading}
                        >
                            <FormattedMessage id="auth.nfc.scanButton" />
                        </Button>
                    )}
                </Stack>
            </DialogContent>
            {scanOpen && (
                <QrScannerDialog
                    open
                    onClose={() => setScanOpen(false)}
                    onDecoded={redeemToken}
                />
            )}
            {nfcOpen && (
                <NfcScanDialog
                    open
                    onClose={() => setNfcOpen(false)}
                    onToken={redeemToken}
                />
            )}
        </Dialog>
    );
}
